import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { LuRadar, LuCheck, LuSettings2, LuLoader, LuImages } from 'react-icons/lu';
import axiosInstance from '../../../api/axiosInstance';
import { Breadcrumbs } from '../../ui';
import CrawlSetupPanel from './CrawlSetupPanel';
import CrawlProgressPanel from './CrawlProgressPanel';
import CrawlResultsPanel from './CrawlResultsPanel';

const PHASES = [
  { key: 'setup', label: 'Setup', icon: LuSettings2 },
  { key: 'crawling', label: 'Crawling', icon: LuRadar },
  { key: 'results', label: 'Results', icon: LuImages }
];

const PHASE_COPY = {
  setup: 'Configure and start UI discovery for this project.',
  crawling: 'We are mapping your application in real time.',
  results: 'Review what the crawler discovered.'
};

const PhaseIndicator = ({ phase }) => {
  const activeIndex = PHASES.findIndex((p) => p.key === phase);

  return (
    <ol className="flex items-center gap-1.5 sm:gap-2" aria-label="Crawl progress">
      {PHASES.map(({ key, label, icon: Icon }, index) => {
        const isDone = index < activeIndex;
        const isActive = index === activeIndex;
        return (
          <li key={key} className="flex items-center gap-1.5 sm:gap-2">
            <span
              aria-current={isActive ? 'step' : undefined}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors sm:px-3 ${
                isActive
                  ? 'bg-[var(--primary)] text-white shadow-sm'
                  : isDone
                    ? 'bg-[var(--success-muted)] text-[color:var(--success)] ring-1 ring-[var(--success)]'
                    : 'bg-[var(--surface)] text-[color:var(--text-muted)] ring-1 ring-[var(--border)]'
              }`}
            >
              {isDone ? <LuCheck size={13} strokeWidth={3} /> : <Icon size={13} />}
              <span className={isActive ? '' : 'hidden sm:inline'}>{label}</span>
            </span>
            {index < PHASES.length - 1 && (
              <span className={`h-px w-4 sm:w-8 ${index < activeIndex ? 'bg-[var(--success)]' : 'bg-[var(--border)]'}`} />
            )}
          </li>
        );
      })}
    </ol>
  );
};

/**
 * In-app UI discovery ("Crawl Project") opened from the projects action menu.
 * One page, three phases: setup -> live crawl progress -> crawl results.
 */
const ProjectCrawlPage = () => {
  const { project_id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projectId = project_id ? parseInt(project_id, 10) : null;

  const [project, setProject] = useState(null);
  const [isProjectLoading, setIsProjectLoading] = useState(true);
  const [phase, setPhase] = useState('setup');
  const [run, setRun] = useState(null);

  useEffect(() => {
    if (!projectId) {
      setIsProjectLoading(false);
      return undefined;
    }
    let cancelled = false;

    axiosInstance
      .get(`/projects/get_project?project_id=${projectId}`)
      .then((response) => {
        if (cancelled) return;
        const data = response?.data || response;
        setProject({ id: projectId, name: data?.project_name || 'Project' });
      })
      .catch((err) => {
        console.error('Error fetching project details:', err);
      })
      .finally(() => {
        if (!cancelled) setIsProjectLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const handleBackToProjects = useCallback(() => {
    navigate('/dashboard/projects');
  }, [navigate]);

  const handleStarted = useCallback((nextRun) => {
    setRun(nextRun);
    setPhase('crawling');
  }, []);

  const handleCompleted = useCallback(() => {
    // Fresh crawl data replaces whatever was cached for this project
    queryClient.invalidateQueries({ queryKey: ['crawler-discovery-overview', projectId] });
    queryClient.invalidateQueries({ queryKey: ['crawler-screenshots', projectId] });
    setPhase('results');
  }, [queryClient, projectId]);

  const handleRestart = useCallback(() => {
    setRun(null);
    setPhase('setup');
  }, []);

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-6xl px-4 py-5 sm:px-6 sm:py-6">
        {/* Header */}
        <header className="mb-5 sm:mb-6">
          <div className="mb-4">
            <Breadcrumbs
              items={[{ label: 'Projects', path: '/dashboard/projects' }, { label: 'Crawl' }]}
              onNavigate={navigate}
            />
          </div>

          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-md">
                <LuRadar size={22} />
              </div>
              <div className="min-w-0">
                <h1 className="flex flex-wrap items-baseline gap-x-2 text-xl font-semibold text-[color:var(--text-primary)] sm:text-2xl">
                  Crawl Project
                  {isProjectLoading ? (
                    <LuLoader size={16} className="animate-spin self-center text-[color:var(--text-subtle)]" />
                  ) : project?.name ? (
                    <span className="truncate text-base font-medium text-[color:var(--text-muted)] sm:text-lg">· {project.name}</span>
                  ) : null}
                </h1>
                <p className="mt-0.5 text-sm text-[color:var(--text-muted)]">{PHASE_COPY[phase]}</p>
              </div>
            </div>
            <PhaseIndicator phase={phase} />
          </div>
        </header>

        {!projectId ? (
          <div className="rounded-2xl border border-[var(--danger)] bg-[var(--danger-muted)] p-6 text-center text-sm text-[color:var(--danger)]">
            Project id is missing.
          </div>
        ) : phase === 'setup' ? (
          <CrawlSetupPanel projectId={projectId} onStarted={handleStarted} onCancel={handleBackToProjects} />
        ) : phase === 'crawling' ? (
          <CrawlProgressPanel
            runId={run?.runId}
            baseUrl={run?.baseUrl}
            onCompleted={handleCompleted}
            onRetry={handleRestart}
          />
        ) : (
          <CrawlResultsPanel projectId={projectId} onNewCrawl={handleRestart} onBack={handleBackToProjects} />
        )}
      </div>
    </div>
  );
};

export default ProjectCrawlPage;
